/**
 * /dashboard/accounting/sales/quotes/$id — Quote detail (G_ACC5).
 *
 * Routes to QuoteDetail which manages edit-mode (DRAFT only) and the
 * state-machine actions (Send / Accept / Decline / Expire / Convert).
 *
 * Migration note (Phase 3e — 2026-06-01): port of
 * frontend/src/app/(dashboard)/dashboard/accounting/sales/quotes/[id]/page.tsx.
 */

import { createFileRoute, Link , useParams} from "@tanstack/react-router";
import { ArrowLeft, ChevronRight } from "lucide-react";
import { toApiError } from "@/lib/api";
import { useQuote } from "@/hooks/use-accounting";
import { AccountingModuleShell } from "@/components/shared/accounting-module-shell";
import { QuoteDetail } from "@/components/accounting/quote-detail";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/components/ui/alert";

export const Route = createFileRoute(
  "/_authgated/app/$tenantSlug/accounting/sales/quotes/$id",
)({
  component: QuoteDetailRoute,
});

function DetailSkeleton() {
  return (
    <div className="space-y-4">
      <Skeleton className="h-24 w-full" />
      <Skeleton className="h-48 w-full" />
    </div>
  );
}

function QuoteDetailRoute() {
  const { tenantSlug } = useParams({ strict: false }) as { tenantSlug: string };
  const { id } = Route.useParams();
  const numId = Number(id);
  const validId = Number.isFinite(numId) && numId > 0;
  const q = useQuote(numId, validId);

  return (
    <AccountingModuleShell
      title="Quote"
      description={q.data?.quote_no}
      actions={
        <Button asChild variant="outline" className="gap-1.5">
          <Link to="/app/$tenantSlug/accounting/sales" params={{ tenantSlug }} search={{ tab: "quotes" }}>
            <ArrowLeft aria-hidden className="h-4 w-4" />
            Back to sales
          </Link>
        </Button>
      }
    >
      {!validId && (
        <Alert variant="destructive">
          <AlertTitle>Invalid quote id</AlertTitle>
          <AlertDescription>
            The URL doesn&apos;t look like a valid quote id.
          </AlertDescription>
        </Alert>
      )}

      {validId && q.isLoading && <DetailSkeleton />}

      {validId && q.isError && (
        <Alert variant="destructive">
          <AlertTitle>Could not load this quote</AlertTitle>
          <AlertDescription>
            {toApiError(q.error).message}{" "}
            <Link
              to="/app/$tenantSlug/accounting/sales" params={{ tenantSlug }}
              search={{ tab: "quotes" }}
              className="ml-1 inline-flex items-center gap-1 font-medium text-primary underline-offset-4 hover:underline"
            >
              Back to quotes
              <ChevronRight aria-hidden className="h-4 w-4" />
            </Link>
          </AlertDescription>
        </Alert>
      )}

      {validId && q.data && <QuoteDetail quote={q.data} />}
    </AccountingModuleShell>
  );
}
